/**
 * Simulation constants.
 *
 * Every value here is READ FROM `data/constants.json`, which is generated from
 * the Assumptions sheet of 02_Balance.xlsx. Nothing is typed in twice: change
 * the spreadsheet, run `pnpm balance`, and the simulation, the client and the
 * server all move together.
 */
import constants from "./data/constants.json" with { type: "json" };
import pkg from "../package.json" with { type: "json" };

/**
 * The simulation version, stamped on every replay.
 *
 * Read from this package's version so that `pnpm changeset` is the ONLY way it
 * moves. Hardcoding it here would let the package version and the replay stamp
 * drift apart, and a replay stamped with a version that never existed cannot be
 * played back correctly by a client that bundles the last three (02 GDD §8).
 *
 * Changing a simulation rule therefore means: change the rule, add a changeset,
 * re-record the goldens. All three in one commit.
 */
export const SIM_VERSION: string = pkg.version;

/** Simulation ticks per second (02 Assumptions). */
export const TICKS_PER_SECOND: number = constants.ticksPerSecond;

/** The playable board is 12x12 (02 Assumptions). */
export const BOARD_SIZE: number = constants.boardSize;

/** The village core is the central 2x2: indices coreMin..coreMax on both axes. */
export const CORE_MIN: number = constants.coreMin;
export const CORE_MAX: number = constants.coreMax;

/** Units still on the road at this tick retreat as survivors. */
export const MAX_BATTLE_TICKS: number = constants.maxBattleTicks;

export const MAX_UNITS_PER_ATTACK: number = constants.maxUnitsPerAttack;
export const MAX_WAVES: number = constants.maxWaves;
export const MAX_WAVE_OFFSET_TICKS: number = constants.maxWaveOffsetTicks;

/**
 * A yardstick for balance work, and nothing else (D-109).
 *
 * This was the Phase 0 attack budget: with no economy and no training, an army
 * had to come from somewhere, so it came from a number. It does not any more —
 * an attack spends the units the player trained, and the server checks the
 * army, not the price.
 *
 * What survives is the one job that number was also quietly doing: making the
 * balance tooling compare armies of equal worth. The sweep's loadouts and the
 * editor's three presets are all sized to this, so when one of them does better
 * than another it is because of what it is made of, not because it was bigger.
 * Nothing in the simulation or the server reads it.
 */
export const REFERENCE_ARMY_VALUE: number = constants.referenceArmyValue;

/** dmg_taken = max(dmg * minDamagePermille/1000, dmg - armor). */
export const MIN_DAMAGE_PERMILLE: number = constants.minDamagePermille;

/** Anti-dominant-strategy rule: no tower type may exceed this share of the slots. */
export const MAX_ONE_TOWER_TYPE_PERMILLE: number = constants.maxOneTowerTypePermille;

/** A tower below this share of its HP stops firing until repaired. */
export const TOWER_OFFLINE_THRESHOLD_PERMILLE: number = constants.towerOfflineThresholdPermille;

export const ROAD_BUDGET_BASE: number = constants.roadBudgetBase;
export const ROAD_BUDGET_PER_WALL_LEVEL: number = constants.roadBudgetPerWallLevel;
export const TOWER_SLOTS_BASE: number = constants.towerSlotsBase;
const TOWER_SLOTS_PER_TWO_EG_LEVELS: number = constants.towerSlotsPerTwoEgLevels;

/**
 * Tower slots come from the Engineer's Guild, not the Wall.
 *
 * The Defence Budget sheet tabulates both buildings on one row, but they are
 * independent — its rows past Wall 10 keep repeating Wall 10 while the Guild
 * climbs to 20. This formula reproduces all 21 rows of that sheet exactly.
 */
export function towerSlots(engineersGuildLevel: number): number {
  const levels = Math.max(0, Math.trunc(engineersGuildLevel));
  return TOWER_SLOTS_BASE + Math.trunc(levels / 2) * TOWER_SLOTS_PER_TWO_EG_LEVELS;
}

/** Anti-dominant-strategy rule: no tower type above half the slots, rounded up. */
export function maxTowersOfOneType(engineersGuildLevel: number): number {
  return Math.ceil((towerSlots(engineersGuildLevel) * MAX_ONE_TOWER_TYPE_PERMILLE) / 1000);
}

/** True when (x, y) is one of the four core tiles. */
export function isCore(x: number, y: number): boolean {
  return x >= CORE_MIN && x <= CORE_MAX && y >= CORE_MIN && y <= CORE_MAX;
}
